import { createReactLogger } from '@vulog-internal/bug-logger-react';

/**
 * Bug Logger ingestion endpoints (Cloud Run). There are only two GCP projects:
 * a `PROD` tenant reports to prod, every other environment reports to dev.
 */
const ENDPOINTS = {
    dev: 'https://bug-logger-api-kfrqeai6oq-ew.a.run.app/v1/events',
    prod: 'https://bug-logger-api-4az2uo7bcq-ew.a.run.app/v1/events',
};

/** Tenant config `env` -> bug-logger release stage (the values the API accepts). */
const STAGE_BY_ENV = {
    DEV: 'dev',
    QA: 'qa',
    STA: 'sta',
    PROD: 'prod',
    DEMO: 'demo',
};

/**
 * Maps the runtime tenant config `env` to a bug-logger release stage. `env` may
 * be `'DEV' | 'QA' | 'STA' | 'PROD' | 'DEMO'` (case-insensitive), or empty /
 * undefined / null; any value we can't resolve falls back to `'dev'`.
 *
 * @param {string} [env] Tenant environment from config.
 * @returns {'dev' | 'qa' | 'sta' | 'prod' | 'demo'}
 */
function resolveStage(env) {
    return STAGE_BY_ENV[String(env).toUpperCase()] || 'dev';
}

/**
 * Selects the ingestion endpoint: only a `PROD` env reports to the prod project;
 * every other environment (dev/qa/sta/demo, empty, unknown) reports to dev.
 *
 * @param {string} [env] Tenant environment from config.
 * @returns {string} the Cloud Run ingestion URL.
 */
function resolveEndpoint(env) {
    return String(env).toUpperCase() === 'PROD' ? ENDPOINTS.prod : ENDPOINTS.dev;
}

/**
 * Client-side publishable ingestion key (access is restricted server-side by
 * allowed origins), so it can safely ship in the bundle.
 */
const API_KEY = '13017775-4ea7-45ea-a12f-c1932d9d04df';

let logger = null;
let initPromise = null;
const subscribers = new Set();

/**
 * Returns the current logger instance, or `null` before initialisation.
 * @returns {import('@vulog-internal/bug-logger-react').ReactLogger | null}
 */
export function getBugLogger() {
    return logger;
}

/**
 * Subscribes to logger availability. The callback fires immediately if a logger
 * already exists, otherwise once, when the logger is first created.
 * @param {(logger: import('@vulog-internal/bug-logger-react').ReactLogger) => void} callback
 * @returns {() => void} an unsubscribe function.
 */
export function subscribeBugLogger(callback) {
    subscribers.add(callback);
    if (logger) {
        callback(logger);
    }
    return () => {
        subscribers.delete(callback);
    };
}

/**
 * Extracts a serialisable HTTP context from an axios error. Axios errors hold
 * circular references (on `request` / `response`), so the raw error must never
 * reach the logger metadata (the transport `JSON.stringify`s the event). We pull
 * a small safe subset (primitive strings from `config`, plus code/status) via
 * axios' `toJSON()` helper — which omits `request`/`response` — never the raw
 * error.
 *
 * @param {{ toJSON?: () => object, response?: { status?: number } }} error
 * @returns {{ http: object } | undefined} metadata section, or undefined for a
 *   non-axios error (no `toJSON`).
 */
function httpMetadata(error) {
    if (!error || typeof error.toJSON !== 'function') {
        return undefined;
    }
    const { code, status, config } = error.toJSON();
    return {
        http: {
            code,
            status: status || (error.response && error.response.status) || undefined,
            method: config && config.method,
            url: config && config.url,
        },
    };
}

/**
 * Severity for an API error: client errors (4xx) are `warning`, everything else
 * (5xx, network errors, no status) is `error`.
 *
 * @param {{ response?: { status?: number } }} error
 * @returns {'warning' | 'error'}
 */
function severityForError(error) {
    const status = error && error.response && error.response.status;
    return status >= 400 && status < 500 ? 'warning' : 'error';
}

/**
 * Reports an axios/API error to the Bug Logger as a handled error (every status
 * is reported), enriched with a safe HTTP context (status/method/url/code) for
 * axios errors. 4xx are reported as `warning`, everything else as `error`.
 * Best-effort: a logger failure must never escape and replace the real API error
 * propagated downstream, and a missing logger (before init / disabled) is a no-op.
 *
 * @param {Error & { response?: { status?: number }, toJSON?: () => object }} error
 */
export function logError(error) {
    if (!logger) {
        return;
    }
    try {
        logger.captureError(error, {
            handled: true,
            severity: severityForError(error),
            metadata: httpMetadata(error),
        });
    } catch (loggingError) {
        console.error('Bug Logger captureError failed', loggingError);
    }
}

/**
 * Creates and initialises the Bug Logger from the runtime tenant config. Safe to
 * call more than once: the first call wins and subsequent calls reuse it. A
 * misconfiguration is logged and swallowed so it never blocks the app.
 *
 * No-op on the local dev server (`NODE_ENV === 'development'`): no logger is
 * created and no events are sent while developing locally. A deployed
 * `build:dev` runs under `NODE_ENV === 'production'`, so it does initialise;
 * which project it reports to depends only on the tenant `env` it is served.
 *
 * @param {object} params
 * @param {string} [params.env] Tenant environment ('DEV'|'QA'|'STA'|'PROD'|'DEMO',
 *   or empty/undefined): drives both the release stage and the endpoint (only
 *   `'PROD'` targets the prod project).
 * @param {string} [params.version] App version (e.g. `REACT_APP_VERSION`).
 * @param {string} [params.client] Tenant identifier (hostname).
 * @returns {Promise<import('@vulog-internal/bug-logger-react').ReactLogger | null>}
 */
export function initBugLogger({ env, version, client }) {
    if (process.env.NODE_ENV === 'development') {
        return Promise.resolve(null);
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = createReactLogger({
        endpoint: resolveEndpoint(env),
        apiKey: API_KEY,
        app: {
            name: 'vulog-fe-fo',
            version: version || 'unknown',
            type: 'web',
            releaseStage: resolveStage(env),
        },
        client,
    })
        .then(instance => {
            logger = instance;
            subscribers.forEach(callback => callback(instance));
            return instance;
        })
        .catch(error => {
            // Never let a logger misconfiguration take the app down; surface it
            // and allow a later retry.
            console.error('Bug Logger init failed', error);
            initPromise = null;
            return null;
        });

    return initPromise;
}
